"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { type ReactNode, useEffect, useRef, useState } from "react";

export type NPSInlineStep = "rate" | "feedback" | "thankyou";

interface NPSInlineProps {
  /** Persist the score immediately when a number is clicked. */
  onSubmitScore: (score: number) => Promise<{ id: string }>;
  /** Attach feedback to an already-saved response id. */
  onSubmitFeedback: (responseId: string, feedback: string) => Promise<void>;
  /**
   * Called after the thank-you step (auto-dismiss).
   * `hasFeedback` is true when the visitor submitted non-empty feedback text.
   */
  onComplete: (details: { score: number; hasFeedback: boolean }) => void;
  /** Notifies the sticky shell when the step changes (for dismiss-stage analytics). */
  onStepChange?: (step: NPSInlineStep) => void;
  className?: string;
}

const FEEDBACK_PLACEHOLDER = "Tell us more (optional)";
const SCORE_RETRY_ERROR = "Couldn't save your rating. Please try again.";
const FEEDBACK_RETRY_ERROR = "Couldn't save your feedback. Please try again.";

function NPSScoreButtons({
  selectedScore,
  isSubmitting,
  onScoreClick,
}: {
  selectedScore: number | null;
  isSubmitting: boolean;
  onScoreClick: (score: number) => void;
}) {
  return (
    <fieldset className="m-0 flex w-full min-w-0 items-stretch gap-1.5 border-0 p-0">
      <legend className="sr-only">Recommendation score from 0 to 10</legend>
      {[...Array(11)].map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onScoreClick(i)}
          disabled={isSubmitting}
          className={cn(
            "h-10 min-w-0 flex-1 rounded-md text-sm font-semibold transition-colors",
            selectedScore === i
              ? "bg-brand-purple text-white"
              : "bg-gray-100 hover:bg-purple-100 text-gray-700",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          aria-label={`Rate ${i} out of 10`}
          aria-pressed={selectedScore === i}
        >
          {i}
        </button>
      ))}
    </fieldset>
  );
}

export function NPSInline({
  onSubmitScore,
  onSubmitFeedback,
  onComplete,
  onStepChange,
  className,
}: NPSInlineProps) {
  const [step, setStep] = useState<NPSInlineStep>("rate");
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const completedRef = useRef<{ score: number; hasFeedback: boolean } | null>(null);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const scheduleComplete = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const details = completedRef.current;
      if (!details) return;
      onComplete(details);
    }, 2000);
  };

  const finishThankYou = (hasFeedback: boolean) => {
    if (selectedScore == null) return;
    setError(null);
    completedRef.current = { score: selectedScore, hasFeedback };
    setStep("thankyou");
    scheduleComplete();
  };

  const handleScoreClick = async (score: number) => {
    if (isSubmitting || step !== "rate") return;

    setSelectedScore(score);
    setError(null);
    setIsSubmitting(true);
    try {
      const { id } = await onSubmitScore(score);
      setResponseId(id);
      setStep("feedback");
    } catch (err) {
      console.error("Error submitting NPS score:", { score, error: err });
      // Keep the survey available — do not mark completed on a transient failure.
      setSelectedScore(null);
      setError(SCORE_RETRY_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (responseId == null || isSubmitting) return;

    const trimmed = feedback.trim();
    if (!trimmed) {
      finishThankYou(false);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmitFeedback(responseId, trimmed);
      finishThankYou(true);
    } catch (err) {
      console.error("Error submitting NPS feedback:", { responseId, error: err });
      // Generic only — never mention PII (that would invite circumvention).
      setError(FEEDBACK_RETRY_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  let content: ReactNode;
  if (step === "thankyou") {
    content = (
      <div className="flex w-full items-center justify-center gap-2 py-1">
        <p className="text-base font-bold text-gray-900">Thank you!</p>
      </div>
    );
  } else if (step === "feedback") {
    content = (
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-wrap items-center gap-3">
          <Input
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={FEEDBACK_PLACEHOLDER}
            maxLength={1024}
            className="min-w-0 flex-1 h-10 text-sm text-gray-900"
            disabled={isSubmitting}
            aria-label={FEEDBACK_PLACEHOLDER}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "nps-inline-error" : undefined}
          />
          <Button
            onClick={handleFeedbackSubmit}
            disabled={isSubmitting}
            className="shrink-0 h-10 px-4"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
        {error ? (
          <p id="nps-inline-error" className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  } else {
    content = (
      <div className="flex w-full flex-col gap-3">
        <p className="text-base font-semibold leading-snug text-gray-900">
          How likely would you recommend PlayMath.org?
        </p>
        <NPSScoreButtons
          selectedScore={selectedScore}
          isSubmitting={isSubmitting}
          onScoreClick={handleScoreClick}
        />
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("box-border w-full bg-white", className)} data-testid="nps-inline">
      {content}
    </div>
  );
}
